/*
 * Observability — Plane VI · §37–§41.
 *
 * Two honest halves, per the deployment reality:
 *
 *  1. METRICS (LIVE). The OTel `mesh.*` surface is exposed by the Transport
 *     sidecar at /v1/metrics in Prometheus TEXT. We parse EVERY `mesh_*`
 *     series and render each as a live MetricTile with a rolling sparkline
 *     built from polling (metrics are poll-derived — there is no series API).
 *     Every tile's value links to its proof: the exact /v1/metrics line it
 *     was read from, surfaced in the ProvabilityDrawer. No tile is fabricated;
 *     an unreadable series renders as a loud "—", never a comforting zero.
 *
 *  2. AUDIT LEDGER (LIVE). GET /v1/audit/entries and GET /v1/audit/verify are
 *     real reads against the audit-plane service, same-origin through the
 *     console proxy. The "verify over range" panel mirrors the CLI
 *     `mesh-cli trust log verify` (OK: 0 errors); an empty ledger (404) or an
 *     unreachable service render their own honest, non-error states. We never
 *     invent a chain head, an OK, or an error count the live call didn't say.
 *
 * The HMAC-erasable right-to-be-forgotten is documented as a concept panel:
 * a forgotten record is cryptographically shredded (its HMAC erasure key is
 * destroyed) yet the hash chain stays intact and verifiable — forgetting
 * without breaking the audit. That action is operator-gated and, since the
 * forgetting-service is down, can only be described, not performed.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  audit,
  isPlaneOffline,
  isMeshApiError,
  MESH_BASE,
  type PlaneOfflineError,
  type AuditVerifyResult,
  type AuditEntriesResponse,
} from "../api/client";
import {
  histogramQuantile,
  selectFamily,
  sumMetric,
  useDerivedSeries,
  useMetricsPoll,
  usePlaneHealth,
  type PlaneHealthState,
} from "../api/metrics";
import type { Claim, MetricSample, Role } from "../api/types";
import { MetricTile } from "../components/MetricTile";
import { SpecRef } from "../components/SpecRef";
import { PlaneState } from "../components/PlaneState";
import { ProvabilityDrawer } from "../components/ProvabilityDrawer";
import { VerifyBadge } from "../components/VerifyBadge";
import { Hash } from "../components/Hash";

/* ---------------------------------------------------------------- *
 * Catalogue of every mesh_* series we surface, with semantic accent
 * and whether a non-zero value is a FAILURE surface (loud red).
 * Colour == meaning: ember = data-in-motion, teal = verified,
 * indigo = structure/lookups, red = failure/denied only.
 * ---------------------------------------------------------------- */

interface SeriesSpec {
  name: string;
  label: string;
  accent: string;
  unit?: string;
  loud?: boolean;
  /** One-line plain meaning, shown in the proof receipt. */
  meaning: string;
}

const SERIES: SeriesSpec[] = [
  {
    name: "mesh_transport_requests_total",
    label: "Requests",
    accent: "var(--ember)",
    unit: "req",
    meaning: "Total dispatches the sidecar has accepted (data-in-motion).",
  },
  {
    name: "mesh_transport_signature_verifications_total",
    label: "Signature verifications",
    accent: "var(--teal)",
    unit: "ver",
    meaning: "RFC 9421 signature verifications performed on inbound dispatch.",
  },
  {
    name: "mesh_transport_queue_pending",
    label: "Queue pending",
    accent: "var(--ember)",
    unit: "msg",
    meaning: "Store-and-forward messages queued but not yet delivered.",
  },
  {
    name: "mesh_transport_peer_key_lookups_total",
    label: "Peer key lookups",
    accent: "var(--indigo)",
    unit: "lkp",
    meaning: "Trust-registry key resolutions for signing/verifying peers.",
  },
  {
    name: "mesh_transport_replay_detections_total",
    label: "Replay detections",
    accent: "var(--red)",
    unit: "hit",
    loud: true,
    meaning: "Replayed (duplicate-nonce) dispatches rejected. Non-zero is a security signal.",
  },
  {
    name: "mesh_transport_dead_letters_total",
    label: "Dead letters",
    accent: "var(--red)",
    unit: "dl",
    loud: true,
    meaning: "Dispatches that exhausted store-and-forward retries. Non-zero needs an operator.",
  },
  {
    name: "mesh_transport_audit_pair_orphans_total",
    label: "Audit-pair orphans",
    accent: "var(--red)",
    unit: "orp",
    loud: true,
    meaning: "Open audit entries with no matching ACK — a chain-integrity warning.",
  },
];

/* The /v1/metrics URL we read everything from — the provenance every tile links to. */
const METRICS_URL = `${MESH_BASE}/v1/metrics`;

export default function Observability({ role }: { role: Role }) {
  const isOperator = role === "operator";
  const { samples, error, loading, lastUpdated, refresh } = useMetricsPoll(5000);
  const [claim, setClaim] = useState<Claim | null>(null);

  /* Audit-ledger verify: bound to the LIVE audit.verifyChain() read. */
  const [verifyState, setVerifyState] = useState<VerifyPhase>({ kind: "idle" });

  /* Audit-ledger entries: bound to the LIVE audit.listEntries() read. */
  const [entriesState, setEntriesState] = useState<EntriesPhase>({ kind: "loading" });

  /* Live health probe of the deployed audit-plane stub. When up, the audit
   * surfaces read "deployed · health 200 · API pending" instead of "not up". */
  const { state: auditHealth } = usePlaneHealth(audit.health);
  const auditHealthy = auditHealth.kind === "up";

  const runVerify = useCallback(() => {
    setVerifyState({ kind: "loading" });
    audit
      .verifyChain()
      .then((data) => setVerifyState({ kind: "ready", data }))
      .catch((e) => {
        if (isPlaneOffline(e)) setVerifyState({ kind: "offline", offline: e });
        else if (isMeshApiError(e) && e.status === 404) {
          setVerifyState({ kind: "empty" });
        } else {
          setVerifyState({
            kind: "error",
            message: e instanceof Error ? e.message : "Unknown error",
          });
        }
      });
  }, []);

  const loadEntries = useCallback(() => {
    setEntriesState({ kind: "loading" });
    audit
      .listEntries()
      .then((data) => setEntriesState({ kind: "ready", data }))
      .catch((e) => {
        if (isPlaneOffline(e)) setEntriesState({ kind: "offline", offline: e });
        else
          setEntriesState({
            kind: "error",
            message: e instanceof Error ? e.message : "Unknown error",
          });
      });
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  /* All mesh_* families actually present in the live scrape, so we can be
   * honest about coverage (which series the sidecar is/ isn't emitting). */
  const presentNames = useMemo(() => {
    if (!samples) return new Set<string>();
    return new Set(samples.filter((s) => s.name.startsWith("mesh_")).map((s) => s.name));
  }, [samples]);

  const metricsFailed = !!error && !samples;

  return (
    <section>
      <header className="view-header">
        <div className="view-header__eyebrow">
          <span className="accent-gold">Observability</span>
          <span><SpecRef text="Plane VI · §37–§41" /></span>
        </div>
        <h1 className="view-header__title">Telemetry &amp; the audit ledger</h1>
        <p className="view-header__sub">
          OTel <code>mesh.*</code> metrics are live from the Transport sidecar;
          the hash-chained audit ledger's entries and verify are now live reads
          too. The HMAC-erasable forgetting service has no read-API yet. Every
          figure links to its source — no badge we cannot substantiate. Role:{" "}
          <strong>{role}</strong>.
        </p>
      </header>

      {/* ---- Live metrics scrape status ---- */}
      <div
        className="panel"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <VerifyBadge
            state={metricsFailed ? "failed" : samples ? "verified" : "pending"}
            label={
              metricsFailed
                ? "Metrics scrape failed"
                : samples
                  ? "Metrics live"
                  : "Reading metrics…"
            }
            size="sm"
            onInspect={() => setClaim(scrapeClaim(metricsFailed, presentNames.size))}
          />
          <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
            {METRICS_URL}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span className="mono" style={{ fontSize: "11px", color: "var(--fg-faint)" }}>
            {lastUpdated
              ? `updated ${new Date(lastUpdated).toLocaleTimeString()}`
              : "—"}
            {loading ? " · polling" : ""}
          </span>
          <button
            type="button"
            onClick={refresh}
            style={{
              minHeight: "44px",
              padding: "0 16px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--hair-strong)",
              background: "var(--bg)",
              color: "var(--fg)",
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {metricsFailed && (
        <div style={{ marginBottom: "16px" }}>
          <PlaneState
            kind="error"
            title="Live metrics unreadable"
            message={`The sidecar at ${METRICS_URL} did not return a parsable Prometheus scrape (${error?.status ?? 0}). Shown loudly rather than hidden — no tiles are fabricated below.`}
            specRef="Part VI §37"
            onRetry={refresh}
          />
        </div>
      )}

      {/* ---- Live metric tiles (one per mesh_* series) ---- */}
      <SectionHeading
        eyebrow="OTel mesh.* · live"
        title="Telemetry"
        note="Each value is parsed from a single /v1/metrics line. Click a value to inspect the exact source line. Series the sidecar does not emit render as “—”, never as zero."
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "14px",
          marginBottom: "28px",
        }}
      >
        {SERIES.map((spec) => (
          <SeriesTile
            key={spec.name}
            spec={spec}
            samples={samples}
            present={presentNames.has(spec.name)}
            scrapeFailed={metricsFailed}
            onInspect={setClaim}
          />
        ))}
        <LatencyTile samples={samples} scrapeFailed={metricsFailed} onInspect={setClaim} />
      </div>

      {/* ---- Audit ledger entries (LIVE) ---- */}
      <SectionHeading
        eyebrow="Audit ledger · §39.3 · live"
        title="Recent audit entries"
        note="Read from GET /v1/audit/entries — the §39.3.1 non-identifying chain-integrity skeleton (seq, event kind, hashes). Identifying columns are gated behind a separate, dual-signature-authorised query and are never served here."
      />
      <div style={{ marginBottom: "28px" }}>
        <AuditEntriesPanel state={entriesState} onRetry={loadEntries} health={auditHealth} onInspect={setClaim} />
      </div>

      {/* ---- Audit-chain "verify over range" (mirrors mesh-cli trust log verify) ---- */}
      <SectionHeading
        eyebrow="Audit ledger · §38 · live"
        title="Verify the chain over a range"
        note="Mirrors the operator CLI: mesh-cli trust log verify. Reads GET /v1/audit/verify — an ADVISORY, self-served result; cross-check the Trust Plane and witnesses per §39 for the authoritative verdict."
      />
      <AuditVerifyPanel
        state={verifyState}
        health={auditHealth}
        onVerify={runVerify}
        onInspect={setClaim}
      />

      {/* ---- HMAC-erasable right-to-be-forgotten (concept) ---- */}
      <SectionHeading
        eyebrow="Forgetting service · §41"
        title="HMAC-erasable right-to-be-forgotten"
        note={
          auditHealthy
            ? "How forgetting works without breaking the audit — described, not performed: the audit service is deployed and healthy, but the forgetting read-API is a pending increment."
            : auditHealth.kind === "probing"
              ? "How forgetting works without breaking the audit — described, not performed. Checking the audit-service health…"
              : "How forgetting works without breaking the audit — described, not performed, because the forgetting-service is offline."
        }
      />
      <ForgettingConceptPanel
        isOperator={isOperator}
        health={auditHealth}
        onInspect={setClaim}
      />

      <ProvabilityDrawer claim={claim} onClose={() => setClaim(null)} />
    </section>
  );
}

/* ---------------------------------------------------------------- *
 * One live series → a MetricTile with a rolling sparkline + proof.
 * ---------------------------------------------------------------- */

function SeriesTile({
  spec,
  samples,
  present,
  scrapeFailed,
  onInspect,
}: {
  spec: SeriesSpec;
  samples: MetricSample[] | null;
  present: boolean;
  scrapeFailed: boolean;
  onInspect: (c: Claim) => void;
}) {
  // Build the rolling series from each poll (metrics are poll-derived).
  const series = useDerivedSeries(samples, (s) => sumMetric(s, spec.name));

  // Honest value: null if scrape failed OR the series isn't emitted at all.
  const value =
    scrapeFailed || !samples ? null : present ? sumMetric(samples, spec.name) : null;

  const family = samples ? selectFamily(samples, spec.name) : [];

  return (
    <MetricTile
      label={spec.label}
      value={value}
      unit={spec.unit}
      accent={spec.accent}
      series={series}
      source={spec.name}
      loud={spec.loud}
      onInspect={() =>
        onInspect(seriesClaim(spec, value, present, scrapeFailed, family))
      }
    />
  );
}

/* Latency p95 derived from the histogram buckets. Honestly an estimate. */
function LatencyTile({
  samples,
  scrapeFailed,
  onInspect,
}: {
  samples: MetricSample[] | null;
  scrapeFailed: boolean;
  onInspect: (c: Claim) => void;
}) {
  const buckets = samples
    ? selectFamily(samples, "mesh_transport_latency_seconds_bucket")
    : [];
  const present = buckets.length > 0;

  const series = useDerivedSeries(samples, (s) => {
    const q = quantileMs(s, 0.95);
    return q ?? NaN;
  });

  const value = scrapeFailed || !samples || !present ? null : quantileMs(samples, 0.95);

  return (
    <MetricTile
      label="Latency p95 (est.)"
      value={value === null || value === undefined ? value : Math.round(value)}
      unit="ms"
      accent="var(--ember)"
      series={series}
      source="mesh_transport_latency_seconds_bucket"
      onInspect={() =>
        onInspect(latencyClaim(value ?? undefined, present, scrapeFailed, buckets.length))
      }
    />
  );
}

/* p95 latency in ms from the histogram buckets. The shared helper returns
 * seconds; we keep the same math so the proof receipt is faithful. */
function quantileMs(samples: MetricSample[], q: number): number | undefined {
  const seconds = histogramQuantile(
    samples,
    "mesh_transport_latency_seconds_bucket",
    "mesh_transport_latency_seconds_count",
    q,
  );
  return seconds === undefined ? undefined : seconds * 1000;
}

/* ---------------------------------------------------------------- *
 * Audit entries panel — LIVE, GET /v1/audit/entries.
 * ---------------------------------------------------------------- */

type EntriesPhase =
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: AuditEntriesResponse };

function AuditEntriesPanel({
  state,
  onRetry,
  health,
  onInspect,
}: {
  state: EntriesPhase;
  onRetry: () => void;
  health: PlaneHealthState;
  onInspect: (c: Claim) => void;
}) {
  if (state.kind === "loading") return <PlaneState kind="loading" />;
  if (state.kind === "offline")
    return (
      <PlaneState
        kind="offline"
        offline={state.offline}
        serviceHealthy={health.kind === "up"}
        serviceProbing={health.kind === "probing"}
        serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
        specRef="Part VI §39.3"
        endpoints={["GET /v1/audit/entries"]}
        onRetry={onRetry}
      />
    );
  if (state.kind === "error")
    return <PlaneState kind="error" message={state.message} onRetry={onRetry} />;

  const { data } = state;
  if (data.entries.length === 0) {
    return (
      <PlaneState
        kind="empty"
        title="No audit entries yet"
        message="The audit ledger is live and answered — nothing has been appended to it yet."
      />
    );
  }

  return (
    <div className="panel" style={{ overflowX: "auto" }}>
      <div className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)", marginBottom: "10px" }}>
        shard {data.shard} · head seq {data.head_seq ?? "—"} · tree size {data.tree_size.toLocaleString()}
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
        {data.entries.map((e) => (
          <li
            key={e.seq}
            className="mono-surface"
            style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}
          >
            <button
              type="button"
              onClick={() =>
                onInspect({
                  label: `Audit entry #${e.seq}`,
                  verdict: "attested",
                  evidence: {
                    hashes: [
                      { label: "self_hash", value: e.self_hash_hex },
                      { label: "prev_hash", value: e.prev_hash_hex },
                    ],
                    specRef: "Part VI §39.3.1",
                    source: "GET /v1/audit/entries",
                    detail: [
                      { label: "seq", value: String(e.seq) },
                      { label: "event_kind", value: e.event_kind },
                      { label: "created_at", value: e.created_at },
                      {
                        label: "note",
                        value:
                          "Identifying columns (operator, request, source/target) are omitted here by design — §39.3.1 gates them behind a separate, dual-signature-authorised query.",
                      },
                    ],
                  },
                })
              }
              style={{ background: "none", border: "none", padding: 0, color: "var(--fg)", cursor: "pointer" }}
            >
              #{e.seq}
            </button>
            <span style={{ color: "var(--fg-muted)" }}>{e.event_kind}</span>
            <span style={{ color: "var(--fg-faint)" }}>{e.created_at}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Audit verify panel — LIVE, GET /v1/audit/verify (advisory).
 * ---------------------------------------------------------------- */

type VerifyPhase =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "offline"; offline: PlaneOfflineError }
  | { kind: "empty" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: AuditVerifyResult };

function AuditVerifyPanel({
  state,
  health,
  onVerify,
  onInspect,
}: {
  state: VerifyPhase;
  health: PlaneHealthState;
  onVerify: () => void;
  onInspect: (c: Claim) => void;
}) {
  const healthy = health.kind === "up";
  const verifying = state.kind === "loading";
  return (
    <div style={{ marginBottom: "28px", display: "flex", flexDirection: "column", gap: "14px" }}>
      <div
        className="panel"
        style={{ display: "flex", flexDirection: "column", gap: "12px" }}
      >
        <div
          className="mono"
          style={{ fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--fg-faint)" }}
        >
          Operator command this panel mirrors
        </div>
        <pre
          className="mono-surface"
          style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "12.5px", lineHeight: 1.6 }}
        >
          $ mesh-cli trust log verify --from &lt;tree_size&gt; --to HEAD{"\n"}
          {/* The expected success line — what we'd render as a teal "Verified". */}
          # expected on success → OK: 0 errors
        </pre>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onVerify}
            disabled={verifying}
            style={{
              minHeight: "44px",
              padding: "0 18px",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--hair-strong)",
              background: "var(--bg)",
              color: "var(--fg)",
              fontFamily: "var(--font-mono)",
              fontSize: "13px",
              cursor: verifying ? "default" : "pointer",
              opacity: verifying ? 0.6 : 1,
            }}
          >
            {verifying ? "Verifying…" : "Run verify over range"}
          </button>
          <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
            GET {MESH_BASE}/v1/audit/verify
          </span>
        </div>
      </div>

      {state.kind === "idle" && (
        <div className="panel" role="status" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <VerifyBadge state="unknown" label="Not yet verified" size="sm" />
          <span style={{ color: "var(--fg-muted)" }}>
            Run the verify to query the live chain. No verdict is shown until the
            chain itself answers.
          </span>
        </div>
      )}

      {state.kind === "loading" && <PlaneState kind="loading" message="Walking the chain…" />}

      {state.kind === "offline" && (
        <PlaneState
          kind="offline"
          offline={state.offline}
          serviceHealthy={healthy}
          serviceProbing={health.kind === "probing"}
          serviceLatencyMs={health.kind === "up" ? health.latencyMs : undefined}
          specRef="Part VI §38"
          endpoints={["GET /v1/audit/verify"]}
          onRetry={onVerify}
        />
      )}

      {state.kind === "empty" && (
        <PlaneState
          kind="empty"
          title="Nothing to verify yet"
          message="The audit ledger is live and answered — it is empty, so there is no chain to walk yet. This is not a failure."
          onRetry={onVerify}
        />
      )}

      {state.kind === "error" && (
        <PlaneState kind="error" message={state.message} onRetry={onVerify} />
      )}

      {state.kind === "ready" && (
        <VerifyResultPanel data={state.data} onInspect={onInspect} />
      )}
    </div>
  );
}

function VerifyResultPanel({
  data,
  onInspect,
}: {
  data: AuditVerifyResult;
  onInspect: (c: Claim) => void;
}) {
  const ok = data.chain_ok && data.signatures_ok;
  // Three honest verdicts, never conflated: OK; keys-not-wired (a deployment
  // gap — verification UNAVAILABLE, gold, non-alarming); genuine divergence
  // (loud red). Rendering "keys unwired" as "chain broken" would either cause
  // a false security incident or train operators to ignore red.
  const keysUnresolved = !ok && data.verification_key_unresolved;
  const claim: Claim = {
    label: "Audit chain verify",
    verdict: ok ? "attested" : keysUnresolved ? "attested" : "failed",
    evidence: {
      specRef: "Part VI §38–§39",
      source: "GET /v1/audit/verify",
      detail: [
        { label: "shard", value: data.shard },
        { label: "range", value: `${data.from_seq}–${data.to_seq}` },
        { label: "chain_ok", value: String(data.chain_ok) },
        { label: "signatures_ok", value: String(data.signatures_ok) },
        {
          label: "first_divergence",
          value: data.first_divergence ? JSON.stringify(data.first_divergence) : "none",
        },
        { label: "advisory", value: data.advisory },
      ],
    },
  };
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <VerifyBadge
          state={ok ? "attested" : keysUnresolved ? "attested" : "failed"}
          label={
            ok
              ? "OK: 0 errors"
              : keysUnresolved
                ? "Verification keys not wired — cannot verify"
                : "Chain divergence detected"
          }
          onInspect={() => onInspect(claim)}
        />
        <span className="mono" style={{ fontSize: "12px", color: "var(--fg-muted)" }}>
          shard {data.shard} · seq {data.from_seq}–{data.to_seq}
        </span>
      </div>
      <p style={{ color: "var(--fg-faint)", margin: 0, fontSize: "12.5px", maxWidth: "60ch" }}>
        {data.advisory}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Forgetting concept panel — HMAC-erasable RTBF, described honestly.
 * ---------------------------------------------------------------- */

function ForgettingConceptPanel({
  isOperator,
  health,
  onInspect,
}: {
  isOperator: boolean;
  health: PlaneHealthState;
  onInspect: (c: Claim) => void;
}) {
  const exampleLeaf = "a3f1c0de9b27e4f8c5a1b6d2e0f93748c2a5b8d1e6f30927a4c1b8e5d20f7361";
  const serviceHealthy = health.kind === "up";
  const probing = health.kind === "probing";
  // When the audit service is deployed & healthy, the forgetting read-API is a
  // pending increment, not "offline". While the probe is in flight we must not
  // flash a false "offline" — say "checking…" instead. Word it honestly.
  const apiNote = serviceHealthy
    ? "forgetting API pending (service deployed & healthy)"
    : probing
      ? "checking forgetting-service health…"
      : "forgetting-service offline";
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "8px" }}>
      <p style={{ color: "var(--fg-muted)", margin: 0, maxWidth: "70ch" }}>
        A forgotten record is <strong>cryptographically shredded</strong>, not
        deleted: its plaintext payload is stored under an HMAC erasure key, and
        forgetting destroys that key. The leaf bytes — and therefore the
        hash-chain — stay byte-identical, so the ledger still verifies end to
        end. The data becomes unrecoverable; the proof of its prior existence
        does not.
      </p>

      <ol
        style={{
          margin: 0,
          paddingLeft: "20px",
          color: "var(--fg-muted)",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          maxWidth: "70ch",
        }}
      >
        <li>Each audit leaf commits to <code>HMAC(K_record, payload)</code>, not the payload.</li>
        <li>Forgetting destroys <code>K_record</code> — the payload can no longer be reconstructed or re-HMAC'd.</li>
        <li>
          The leaf hash, RFC 6962 inclusion proof, and chain links are unchanged,
          so <code>verify</code> still returns <span className="accent-teal">OK: 0 errors</span>.
        </li>
      </ol>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          fontSize: "12.5px",
        }}
      >
        <span className="mono" style={{ color: "var(--fg-faint)" }}>
          chain stays intact:
        </span>
        <Hash value={exampleLeaf} accent="var(--gold)" label="leaf" />
        <VerifyBadge
          state="attested"
          label="Shredded · chain intact"
          size="sm"
          onInspect={() => onInspect(forgettingClaim(exampleLeaf))}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          borderTop: "1px solid var(--hair)",
          paddingTop: "14px",
        }}
      >
        <button
          type="button"
          disabled
          aria-disabled="true"
          title={
            isOperator
              ? `POST /v1/audit/forget — ${apiNote}`
              : `Operator role required, and the ${apiNote}`
          }
          style={{
            minHeight: "44px",
            padding: "0 18px",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--hair-strong)",
            background: "var(--bg)",
            color: "var(--fg-faint)",
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            cursor: "not-allowed",
            opacity: 0.7,
          }}
        >
          Forget a record…
        </button>
        <span
          className="mono"
          style={{
            fontSize: "11.5px",
            color: serviceHealthy ? "var(--indigo)" : "var(--fg-faint)",
          }}
        >
          {isOperator
            ? `POST /v1/audit/forget · ${apiNote}`
            : `operator-gated · also: ${apiNote}`}
        </span>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Small presentational helper.
 * ---------------------------------------------------------------- */

function SectionHeading({
  eyebrow,
  title,
  note,
}: {
  eyebrow: string;
  title: string;
  note: string;
}) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <div
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--indigo)",
          marginBottom: "4px",
        }}
      >
        {eyebrow}
      </div>
      <h2 style={{ fontSize: "20px", margin: "0 0 6px" }}>{title}</h2>
      <p style={{ color: "var(--fg-faint)", margin: 0, fontSize: "13px", maxWidth: "78ch" }}>
        {note}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * Claim builders — evidence linking each surface to its source.
 * A claim with NO evidence renders as "unsubstantiated" — intentional.
 * ---------------------------------------------------------------- */

function scrapeClaim(failed: boolean, presentCount: number): Claim {
  return {
    label: "Live metrics scrape",
    verdict: failed ? "failed" : "verified",
    evidence: {
      detail: [
        { label: "endpoint", value: "GET /v1/metrics" },
        { label: "format", value: "Prometheus TEXT (parsed)" },
        { label: "mesh_* series present", value: failed ? "0 (scrape failed)" : String(presentCount) },
      ],
      specRef: "Part VI §37",
      source: METRICS_URL,
    },
  };
}

function seriesClaim(
  spec: SeriesSpec,
  value: number | null,
  present: boolean,
  scrapeFailed: boolean,
  family: MetricSample[],
): Claim {
  if (scrapeFailed) {
    return {
      label: spec.label,
      verdict: "failed",
      evidence: {
        detail: [
          { label: "series", value: spec.name },
          { label: "status", value: "scrape failed — value not read" },
        ],
        specRef: "Part VI §37",
        source: METRICS_URL,
      },
    };
  }
  if (!present) {
    return {
      label: spec.label,
      verdict: "unknown",
      evidence: {
        detail: [
          { label: "series", value: spec.name },
          { label: "status", value: "not emitted by this sidecar build" },
        ],
        specRef: "Part VI §37",
        source: METRICS_URL,
      },
    };
  }
  const lines = family.map((s) => ({
    label: renderLabels(s.labels) || "(no labels)",
    value: Number.isFinite(s.value) ? String(s.value) : "NaN",
  }));
  return {
    label: spec.label,
    verdict: spec.loud && (value ?? 0) > 0 ? "failed" : "verified",
    evidence: {
      detail: [
        { label: "series", value: spec.name },
        { label: "value", value: value === null ? "—" : `${value} ${spec.unit ?? ""}`.trim() },
        { label: "meaning", value: spec.meaning },
        ...lines,
      ],
      specRef: "Part VI §37",
      source: `${METRICS_URL}  →  ${spec.name}`,
    },
  };
}

function latencyClaim(
  ms: number | undefined,
  present: boolean,
  scrapeFailed: boolean,
  bucketCount: number,
): Claim {
  if (scrapeFailed || !present) {
    return {
      label: "Latency p95 (est.)",
      verdict: scrapeFailed ? "failed" : "unknown",
      evidence: {
        detail: [
          { label: "series", value: "mesh_transport_latency_seconds_bucket" },
          {
            label: "status",
            value: scrapeFailed ? "scrape failed" : "no histogram buckets emitted",
          },
        ],
        specRef: "Part VI §37",
        source: METRICS_URL,
      },
    };
  }
  return {
    label: "Latency p95 (est.)",
    verdict: "attested",
    evidence: {
      detail: [
        { label: "method", value: "linear interpolation across histogram buckets" },
        { label: "buckets", value: `${bucketCount} le-bounded` },
        { label: "p95 estimate", value: ms === undefined ? "—" : `${Math.round(ms)} ms` },
        { label: "honesty", value: "estimate — not an exact percentile" },
      ],
      specRef: "Part VI §37",
      source: `${METRICS_URL}  →  mesh_transport_latency_seconds_{bucket,count}`,
    },
  };
}

function forgettingClaim(leaf: string): Claim {
  return {
    label: "Forgetting preserves the chain",
    verdict: "attested",
    evidence: {
      hashes: [{ label: "leaf (unchanged after shred)", value: leaf }],
      algorithm: "HMAC erasure key (per-record), RFC 6962 Merkle leaf",
      detail: [
        { label: "shredded", value: "K_record destroyed → payload unrecoverable" },
        { label: "chain", value: "leaf bytes + inclusion proof unchanged" },
        { label: "verify", value: "still returns OK: 0 errors" },
        { label: "status", value: "concept — forgetting-service offline, not performed" },
      ],
      specRef: "Part VI §41 · Consent Part VII §43.2",
    },
  };
}

function renderLabels(labels: Record<string, string>): string {
  const entries = Object.entries(labels);
  if (entries.length === 0) return "";
  return entries.map(([k, v]) => `${k}="${v}"`).join(", ");
}
